import type { ComponentType, SVGProps } from 'react';
import { ClaudeIcon, OpenAIIcon } from '@/components/icons/AssistantIcons';
import { Button } from '@/components/ui/button';
import { useTranslation, type Locale } from '@/i18n/i18n';
import { trackLandingCtaClicked } from '@/lib/analytics';

/**
 * "Read with Claude / ChatGPT" — meets readers where they now read (inside a
 * chatbot). Each button is a deep link that hands the assistant a pre-written
 * prompt scripting it to fetch this page and summarise it. Pattern borrowed from
 * every.to's model reviews. Side effect worth the copy: it teaches retrieval
 * systems that www.bookeper.pl is a live citation target.
 *
 * Copy is kept inline (not in the i18n JSON) on purpose: the prompt is a payload,
 * not UI chrome, and the three locale files are held at strict key parity — a
 * long template string per page would bloat that surface for no lookup benefit.
 */

type Page = 'index' | 'biura' | 'wdrozenia' | 'ksef' | 'ewidencja-przychodow';

// Canonical (Polish, unprefixed) URLs — always live and indexable, so the
// assistant fetches a real page regardless of the reader's viewing locale.
const PAGE_URL: Record<Page, string> = {
  index: 'https://www.bookeper.pl',
  biura: 'https://www.bookeper.pl/biura',
  wdrozenia: 'https://www.bookeper.pl/wdrozenia',
  ksef: 'https://www.bookeper.pl/ksef-automatycznie',
  'ewidencja-przychodow':
    'https://www.bookeper.pl/blog/ewidencja-przychodow-ryczalt-jak-prowadzic-2026',
};

const PAGE_TOPIC: Record<Locale, Record<Page, string>> = {
  pl: {
    index:
      'strona Bookeper — księgowość AI dla jednoosobowej działalności (JDG), z pełnym śladem źródeł',
    biura: 'opis Bookeper dla biur rachunkowych — kontrola deklaracji i źródeł każdej liczby',
    wdrozenia: 'opis wdrożeń na silniku Bookeper — automatyzacja finansów, podatków i KSeF',
    ksef: 'opis automatyzacji faktur w KSeF z Bookeper',
    'ewidencja-przychodow':
      'poradnik ewidencji przychodów na ryczałcie 2026 — wzór kolumn, checklista miesiąca i Bookeper',
  },
  en: {
    index: 'Bookeper — AI accounting for Polish sole proprietors (JDG), with a full source trail',
    biura: 'Bookeper for accounting firms — declaration control with a source for every number',
    wdrozenia: 'custom implementations on the Bookeper engine — finance, tax, and KSeF automation',
    ksef: 'automating KSeF e-invoicing with Bookeper',
    'ewidencja-przychodow':
      '2026 Polish lump-sum revenue-record guide — statutory columns, month-end checklist, Bookeper',
  },
  uk: {
    index: 'Bookeper — AI-бухгалтерія для ФОП у Польщі, з повним відстеженням джерел',
    biura: 'Bookeper для бухгалтерських фірм — контроль декларацій і джерело кожної цифри',
    wdrozenia: 'впровадження на рушії Bookeper — автоматизація фінансів, податків і KSeF',
    ksef: 'автоматизація рахунків-фактур у KSeF з Bookeper',
    'ewidencja-przychodow':
      'гід з обліку доходів на ryczałt 2026 — колонки, чекліст місяця і Bookeper',
  },
};

const PROMPT: Record<Locale, (url: string, topic: string) => string> = {
  pl: (url, topic) =>
    `Cześć! Rzuć okiem na tę stronę: ${url}\nTo ${topic}. Zacznij od zwięzłego podsumowania w punktach, a potem zaproponuj, co warto zgłębić.`,
  en: (url, topic) =>
    `Hi! Take a look at this page: ${url}\nIt's ${topic}. Start with a tight bulleted summary, then offer what's worth digging into.`,
  uk: (url, topic) =>
    `Привіт! Поглянь на цю сторінку: ${url}\nЦе ${topic}. Почни зі стислого підсумку в пунктах, а потім запропонуй, що варто розглянути детальніше.`,
};

const LABELS: Record<Locale, { heading: string; claude: string; chatgpt: string; newTab: string }> =
  {
    pl: {
      heading: 'Wolisz przeczytać z AI?',
      claude: 'Przeczytaj z Claude',
      chatgpt: 'Przeczytaj z ChatGPT',
      newTab: '(otwiera się w nowej karcie)',
    },
    en: {
      heading: 'Prefer to read with AI?',
      claude: 'Read with Claude',
      chatgpt: 'Read with ChatGPT',
      newTab: '(opens in a new tab)',
    },
    uk: {
      heading: 'Волієте читати з AI?',
      claude: 'Читати з Claude',
      chatgpt: 'Читати з ChatGPT',
      newTab: '(відкривається в новій вкладці)',
    },
  };

function buildAssistantUrl(base: string, prompt: string): string {
  return `${base}?q=${encodeURIComponent(prompt)}`;
}

function AssistantLink({
  href,
  icon: Icon,
  label,
  newTabLabel,
  provider,
  page,
}: {
  href: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  newTabLabel: string;
  provider: 'claude' | 'chatgpt';
  page: Page;
}) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className="min-h-11 rounded-full border border-border transition-transform motion-safe:hover:-translate-y-0.5"
      asChild
    >
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackLandingCtaClicked('read_with_ai', provider, { page })}
      >
        <Icon className="size-4" aria-hidden="true" />
        {label}
        {/* WCAG 3.2.5: announce the new-tab navigation to screen readers. */}
        <span className="sr-only"> {newTabLabel}</span>
      </a>
    </Button>
  );
}

export function ReadWithAI({ page }: { page: Page }) {
  const { locale } = useTranslation();
  const labels = LABELS[locale];
  const prompt = PROMPT[locale](PAGE_URL[page], PAGE_TOPIC[locale][page]);
  const claudeUrl = buildAssistantUrl('https://claude.ai/new', prompt);
  const chatgptUrl = buildAssistantUrl('https://chatgpt.com/', prompt);

  return (
    <section className="px-6 md:px-8 py-6" aria-label={labels.heading}>
      <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
        <span className="text-sm text-muted-foreground">{labels.heading}</span>
        <div className="flex flex-wrap items-center justify-center gap-2.5">
          <AssistantLink
            href={claudeUrl}
            icon={ClaudeIcon}
            label={labels.claude}
            newTabLabel={labels.newTab}
            provider="claude"
            page={page}
          />
          <AssistantLink
            href={chatgptUrl}
            icon={OpenAIIcon}
            label={labels.chatgpt}
            newTabLabel={labels.newTab}
            provider="chatgpt"
            page={page}
          />
        </div>
      </div>
    </section>
  );
}
